import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Menu, Tooltip, Typography } from 'antd';

import classNames from 'classnames';
import { useSelector } from 'react-redux';
import {
  getActiveMailFolderId,
  getIsSettingEnabled,
  getLastOpenDriveItemFolder,
  getUserMe,
  RootReducerState,
} from '../../../apps/main/rootReducer';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { TFunction } from 'i18next';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import { MailFolderId } from '../../../models/Types';
import { ProjectNewsCounts } from '../../../models/Project';
import { Project } from '../../../models/Project';
import {
  ExtensionUserState,
  ProjectExtensionType,
} from '../../../models/ProjectExtension';
import { getProjectAccess } from '../utils';

const CLASSNAME = 'prio-project-menu';

const getProjectExtensionMe = (
  project: Project,
  type: ProjectExtensionType
) => {
  if (!project) {
    return undefined;
  }
  return project.projectExtensionMeDtos?.find(
    (ext) => ext.projectExtensionType === type
  );
};

const isExtensionLoading = (state: ExtensionUserState | undefined) => {
  if (!state) {
    return false;
  }
  return state === 'waitingForAccess' || state === 'waitingForSetup';
};

const isExtensionDisabled = (state: ExtensionUserState | undefined) => {
  if (!state) {
    return false;
  }
  return state !== 'ready';
};

interface ProjectMenuProps {
  projectId: string;
  className?: string;
  selectedProjectId?: string;
  selectedSubModule?: string;
  masterPlanId?: string;
  groupId?: string;
  emailPrefix?: string;
  emailSuffix?: string;
  pathPrefix?: string;
  collapsed?: boolean;
  projectNewsCounts?: ProjectNewsCounts;
  activeTab: string;
  getProjectById: (projectId: string) => Project | undefined;
}

export const ProjectMenu: React.FC<ProjectMenuProps> = (props) => {
  //#region ------------------------------ Defaults
  const {
    projectId,
    selectedProjectId,
    selectedSubModule,
    emailSuffix,
    emailPrefix,
    pathPrefix,
    collapsed,
    activeTab,
    className,
    projectNewsCounts,
    getProjectById,
    // oneDrivePath,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const userMe = useSelector(getUserMe);

  const project = useMemo(() => {
    return getProjectById(projectId);
  }, [projectId, getProjectById]);
  const isFinanceEnabled = useSelector<RootReducerState, boolean>((state) =>
    getIsSettingEnabled(state, 'General_FinanceServicesEnabled')
  );

  const sharedMailboxProjectExtensionMeState = useMemo(() => {
    return getProjectExtensionMe(project, 'sharedMailbox')?.extensionUserState;
  }, [project]);

  const sharePointProjectExtensionMeState = useMemo(() => {
    return getProjectExtensionMe(project, 'sharepoint')?.extensionUserState;
  }, [project]);

  const plannerProjectExtensionMeState = useMemo(() => {
    return getProjectExtensionMe(project, 'planner')?.extensionUserState;
  }, [project]);

  const promanProjectExtensionMeState = useMemo(() => {
    return getProjectExtensionMe(project, 'proman')?.extensionUserState;
  }, [project]);

  const openExperienceProjectExtensionMeState = useMemo(() => {
    return getProjectExtensionMe(project, 'openExperience')?.extensionUserState;
  }, [project]);

  const amberSearchProjectExtensionMeState = useMemo(() => {
    return getProjectExtensionMe(project, 'amberSearch')?.extensionUserState;
  }, [project]);

  const isSelectedProject = selectedProjectId === projectId;

  const activeMailFolderId = useSelector<RootReducerState, MailFolderId>(
    (state) => getActiveMailFolderId(state, projectId)
  );
  //#endregion

  const lastOpenFolderDriveItemIds = useSelector((state: RootReducerState) =>
    getLastOpenDriveItemFolder(state, projectId)
  );

  const accessRights = useMemo(() => {
    return getProjectAccess(project, userMe, activeTab);
  }, [project, userMe, activeTab]);

  return (
    <Menu
      selectedKeys={isSelectedProject ? [selectedSubModule] : []}
      className={classNames(`${CLASSNAME}-menu`, className, {
        [`${CLASSNAME}-menuCollapsed`]: collapsed,
      })}
      mode="inline"
      inlineIndent={theme.old.components.menuInlineIndent}
    >
      {navItem(
        'summary',
        'tachometer-alt',
        t,
        theme,
        projectId,
        pathPrefix,
        collapsed
      )}
      {accessRights.sharedMailboxUser &&
        navItem(
          'mail',
          'envelope',
          t,
          theme,
          projectId,
          pathPrefix,
          collapsed,
          projectNewsCounts?.messageUnread ||
            projectNewsCounts?.messageAssignmentUnread
            ? true
            : false,
          activeMailFolderId ?? undefined,
          sharedMailboxProjectExtensionMeState
        )}
      {accessRights.contacts &&
        navItem(
          'contacts',
          'users',
          t,
          theme,
          projectId,
          pathPrefix,
          collapsed
        )}
      {accessRights.tasks && (
        <Menu.Item
          key="planner"
          className={classNames(`${CLASSNAME}-menuItemPlanner`, {
            [`${CLASSNAME}-itemCollapsed`]: collapsed,
          })}
          disabled={isExtensionDisabled(plannerProjectExtensionMeState)}
          title={
            <>
              {extensionStateTooltipWrapper({
                projectExtensionMeState: plannerProjectExtensionMeState,
                children: (
                  <FontAwesomeIcon
                    fixedWidth
                    icon={[
                      'fal',
                      isExtensionLoading(plannerProjectExtensionMeState)
                        ? 'spinner-third'
                        : 'tasks',
                    ]}
                    spin={isExtensionLoading(plannerProjectExtensionMeState)}
                    className={`${CLASSNAME}-icon`}
                  />
                ),
                t,
                addInName: t('projects:navigation.planner'),
              })}
              <Typography.Text className={`${CLASSNAME}-navItemText`}>
                {t('projects:navigation.planner')}
              </Typography.Text>
            </>
          }
        >
          {isExtensionDisabled(plannerProjectExtensionMeState) ? (
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div style={{ flex: 1 }}>
                {extensionStateTooltipWrapper({
                  projectExtensionMeState: plannerProjectExtensionMeState,
                  children: (
                    <FontAwesomeIcon
                      fixedWidth
                      icon={[
                        'fal',
                        isExtensionLoading(plannerProjectExtensionMeState)
                          ? 'spinner-third'
                          : 'tasks',
                      ]}
                      spin={isExtensionLoading(plannerProjectExtensionMeState)}
                      className={`${CLASSNAME}-icon`}
                    />
                  ),
                  t,
                  addInName: t('projects:navigation.planner'),
                })}
                {!collapsed && (
                  <Typography.Text className={`${CLASSNAME}-navItemText`}>
                    {t('projects:navigation.planner')}
                  </Typography.Text>
                )}
              </div>
            </div>
          ) : (
            <a
              href={`https://planner.cloud.microsoft/webui/plan/${project.masterPlanId}/view/board`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <div style={{ flex: 1 }}>
                  {extensionStateTooltipWrapper({
                    projectExtensionMeState: plannerProjectExtensionMeState,
                    children: (
                      <FontAwesomeIcon
                        fixedWidth
                        icon={[
                          'fal',
                          isExtensionLoading(plannerProjectExtensionMeState)
                            ? 'spinner-third'
                            : 'tasks',
                        ]}
                        spin={isExtensionLoading(
                          plannerProjectExtensionMeState
                        )}
                        className={`${CLASSNAME}-icon`}
                      />
                    ),
                    t,
                    addInName: t('projects:navigation.planner'),
                  })}
                  {!collapsed && (
                    <Typography.Text className={`${CLASSNAME}-navItemText`}>
                      {t('projects:navigation.planner')}
                    </Typography.Text>
                  )}
                </div>
                <div>
                  <FontAwesomeIcon
                    fixedWidth
                    icon={['fal', 'external-link-alt']}
                    className={classNames(
                      'prio-project-menu-external-link',
                      `${CLASSNAME}-iconPlanner`
                    )}
                  />
                </div>
              </div>
            </a>
          )}
        </Menu.Item>
      )}
      {accessRights.calendar && (
        <Menu.Item
          key="calendar"
          className={classNames(`${CLASSNAME}-menuItemPlanner`, {
            [`${CLASSNAME}-itemCollapsed`]: collapsed,
          })}
          title={
            <>
              <FontAwesomeIcon
                fixedWidth
                icon={['fal', 'calendar']}
                className={`${CLASSNAME}-icon`}
              />
              <Typography.Text className={`${CLASSNAME}-navItemText`}>
                {t('projects:navigation.calendar')}
              </Typography.Text>
            </>
          }
        >
          <a
            href={`https://outlook.office.com/calendar/${emailPrefix}@${emailSuffix}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div style={{ flex: 1 }}>
                <FontAwesomeIcon
                  fixedWidth
                  icon={['fal', 'calendar']}
                  className={`${CLASSNAME}-icon`}
                />
                {!collapsed && (
                  <Typography.Text className={`${CLASSNAME}-navItemText`}>
                    {t('projects:navigation.calendar')}
                  </Typography.Text>
                )}
              </div>
              <div>
                <FontAwesomeIcon
                  fixedWidth
                  icon={['fal', 'external-link-alt']}
                  className={classNames(
                    'prio-project-menu-external-link',
                    `${CLASSNAME}-iconPlanner`
                  )}
                />
              </div>
            </div>
          </a>
        </Menu.Item>
      )}
      {accessRights.timeRecord &&
        isFinanceEnabled &&
        navItem(
          'timeRecord',
          'user-clock',
          t,
          theme,
          projectId,
          pathPrefix,
          collapsed
        )}
      {accessRights.proman &&
        navItem(
          'proman',
          'clipboard-list',
          t,
          theme,
          projectId,
          pathPrefix,
          collapsed,
          null,
          null,
          promanProjectExtensionMeState
        )}
      {accessRights.openExperience &&
        navItem(
          'openexperience',
          'building',
          t,
          theme,
          projectId,
          pathPrefix,
          collapsed,
          null,
          null,
          openExperienceProjectExtensionMeState
        )}
      {accessRights.amberSearch &&
        navItem(
          'amberSearch',
          'radar',
          t,
          theme,
          projectId,
          pathPrefix,
          collapsed,
          null,
          null,
          amberSearchProjectExtensionMeState
        )}
      {accessRights.groupId &&
        navItem(
          'documents',
          'folder-tree',
          t,
          theme,
          projectId,
          pathPrefix,
          collapsed,
          false,
          lastOpenFolderDriveItemIds?.documentsPage &&
            `folder/${lastOpenFolderDriveItemIds?.documentsPage}`,
          sharePointProjectExtensionMeState
        )}
      {accessRights.invoiceControlling &&
        isFinanceEnabled &&
        navItem(
          'invoiceControlling',
          'euro-sign',
          t,
          theme,
          projectId,
          pathPrefix,
          collapsed
        )}
      {accessRights.settings &&
        navItem(
          'settings',
          'cog',
          t,
          theme,
          projectId,
          pathPrefix,
          collapsed,
          undefined,
          'general'
        )}
    </Menu>
  );
};

export default ProjectMenu;

const extensionStateTooltipWrapper = (props: {
  projectExtensionMeState: ExtensionUserState;
  children: React.ReactNode;
  t: TFunction;
  addInName: string;
}) => {
  const { projectExtensionMeState, children, addInName, t } = props;
  if (isExtensionLoading(projectExtensionMeState)) {
    return (
      <Tooltip
        title={t(
          `projects:navigation.tooltip.projectExtension.${projectExtensionMeState}`,
          {
            addIn: addInName,
          }
        )}
        placement="right"
      >
        {children}
      </Tooltip>
    );
  }
  return <>{children}</>;
};

const navItem = (
  itemKey: string,
  icon: IconName,
  t: TFunction,
  theme: PrioTheme,
  projectId: string,
  urlPrefix?: string,
  collapsed?: boolean,
  showBadge?: boolean,
  suffix?: string,
  projectExtensionMeState?: ExtensionUserState
) => {
  const isLoading = isExtensionLoading(projectExtensionMeState);

  const disabled = isExtensionDisabled(projectExtensionMeState);

  const itemContent = (
    <>
      {extensionStateTooltipWrapper({
        projectExtensionMeState,
        children: (
          <FontAwesomeIcon
            fixedWidth
            icon={['fal', isLoading ? 'spinner-third' : icon]}
            spin={isLoading}
            className={`${CLASSNAME}-icon`}
          />
        ),
        t,
        addInName: t(`projects:navigation.${itemKey}`),
      })}

      {!collapsed && (
        <Typography.Text className={`${CLASSNAME}-navItemText`}>
          {t(`projects:navigation.${itemKey}`)}
        </Typography.Text>
      )}
    </>
  );

  const link = disabled ? (
    itemContent
  ) : (
    <Link
      to={`${urlPrefix ?? ''}${projectId}/${itemKey}${
        suffix ? `/${suffix}` : ''
      }`}
      className={showBadge ? `${CLASSNAME}-flex1` : undefined}
    >
      {itemContent}
    </Link>
  );

  return (
    <Menu.Item
      key={itemKey}
      className={classNames({
        [`${CLASSNAME}-itemShowBadge`]: !collapsed && showBadge,
      })}
      disabled={disabled}
      title={
        <>
          {icon !== null ? (
            <FontAwesomeIcon
              fixedWidth
              icon={['fal', icon]}
              className={`${CLASSNAME}-icon`}
            />
          ) : (
            <span className={`${CLASSNAME}-settingsIcon`} />
          )}
          <Typography.Text className={`${CLASSNAME}-navItemText`}>
            {t(`projects:navigation.${itemKey}`)}
          </Typography.Text>
        </>
      }
    >
      {collapsed && showBadge ? (
        <Badge
          className={`${CLASSNAME}-badge`}
          dot
          color={theme.old.palette.primaryColor}
        >
          {link}
        </Badge>
      ) : (
        link
      )}
      {!collapsed && showBadge ? (
        <Badge
          className={`${CLASSNAME}-badgeStatus`}
          dot
          color={theme.old.palette.primaryColor}
        ></Badge>
      ) : null}
    </Menu.Item>
  );
};
